import { Component, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { TextareaModule } from 'primeng/textarea';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { ToastModule } from 'primeng/toast';
import { CheckboxModule } from 'primeng/checkbox';
import { RadioButtonModule } from 'primeng/radiobutton';
import { TagModule } from 'primeng/tag';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService, ConfirmationService } from 'primeng/api';
import { Products } from '../../../../core/services/products.service';
import { AttributesService } from '../../../../core/services/attributes.service';
import { TipoMonedaService } from '../../../../core/services/tipo-moneda.service';
import { Product, ProductAdminItem } from '../../../../shared/models/Product';
import { Brand, Category, Size, Color } from '../../../../shared/models/Attribute';
import { TipoMoneda } from '../../../../shared/models/system';

@Component({
  selector: 'app-products-admin',
  standalone: true,
  imports: [
    CommonModule, FormsModule, TableModule, ButtonModule,
    InputTextModule, DialogModule, InputNumberModule, TextareaModule,
    SelectModule, MultiSelectModule, ToastModule, ConfirmDialogModule,
    TooltipModule, CheckboxModule, RadioButtonModule, TagModule
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './products-admin.component.html',
  styleUrls: ['./products-admin.component.css']
})
export class ProductsAdminComponent implements OnInit, OnDestroy {

  products: ProductAdminItem[] = [];
  loading = true;
  first = 0;
  rows = 10;
  totalRecords = 0;
  search = '';
  activeFilter: boolean | null = null;
  activeOptions = [
    { label: 'Activos', value: true },
    { label: 'Inactivos', value: false }
  ];
  private searchTimer: ReturnType<typeof setTimeout> | null = null;
  private requestSeq = 0;

  product: any = {};
  sizeDetailsMap: { [key: number]: { quantity: number, price: number } } = {};

  modelsList: any[] = [];
  filteredModels: any[] = [];
  categories: Category[] = [];
  brands: Brand[] = [];
  sizesList: Size[] = [];
  colorsList: Color[] = [];
  tiposMoneda: TipoMoneda[] = [];

  selectedColorId: number | null = null;
  productDialog: boolean = false;
  submitted: boolean = false;

  viewProductDialog: boolean = false;
  selectedProductView: any = null;
  priceDetailsDialog: boolean = false;

  offerDialog: boolean = false;
  selectedProductForOffer: any = null;
  offerData: any = {
    isOfferActive: false,
    offerScope: 'GLOBAL',
    globalDiscountType: 'S/',
    globalDiscountValue: 0,
    sizeSpecificOffers: []
  };

  constructor(
    private productService: Products,
    private attrService: AttributesService,
    private tipoMonedaService: TipoMonedaService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.loadAttributes();
  }

  ngOnDestroy() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
  }

  scheduleServerLoad(): void {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.reloadFirst(), 350);
  }

  reloadFirst(): void {
    this.first = 0;
    this.loadProducts({ first: 0, rows: this.rows });
  }

  loadProducts(event?: TableLazyLoadEvent) {
    const seq = ++this.requestSeq;
    this.loading = true;
    const first = event?.first ?? this.first;
    const size = event?.rows || this.rows;
    this.first = first || 0;
    this.rows = size;
    const page = this.rows > 0 ? Math.floor(this.first / this.rows) : 0;

    this.productService.getAdminPage(page, this.rows, this.search, this.activeFilter).subscribe({
      next: (data) => {
        if (seq !== this.requestSeq) return;
        this.products = data?.content || [];
        this.totalRecords = data?.totalElements || 0;
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        if (seq !== this.requestSeq) return;
        this.products = [];
        this.totalRecords = 0;
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudo cargar el catálogo'
        });
        this.cdr.detectChanges();
      }
    });
  }

  listPrice(product: ProductAdminItem): number {
    if (product.isOffer && product.priceOffer != null) return product.priceOffer;
    return product.price;
  }

  showsStruckPrice(product: ProductAdminItem): boolean {
    return !!product.isOffer && product.priceOffer != null;
  }

  offerBySize(product: ProductAdminItem): boolean {
    return !!product.isOffer && product.offerScope === 'BY_SIZE' && product.priceOffer == null;
  }

  loadAttributes() {
    this.attrService.getActiveBrands().subscribe(data => this.brands = data);
    this.attrService.getActiveCategories().subscribe(data => this.categories = data);
    this.attrService.getActiveSizes().subscribe(data => this.sizesList = data);
    this.attrService.getActiveColors().subscribe(data => this.colorsList = data);
    this.tipoMonedaService.getActive().subscribe(data => this.tiposMoneda = data);
    this.attrService.getActiveModels().subscribe({
      next: (data) => {
        this.modelsList = data;
        if (this.product && this.product.categoryId) {
          this.filterModelsByCategoryId(this.product.categoryId);
        }
      },
    });
  }

  filterModelsByCategoryId(categoryId: number) {
    this.filteredModels = this.modelsList.filter(m => m.categoryId == categoryId);
  }

  onCategoryChange(event: any) {
    const categoryId = event.value;
    this.product.modelId = null;
    if (categoryId) {
      this.filterModelsByCategoryId(categoryId);
    } else {
      this.filteredModels = [];
    }
    this.cdr.detectChanges();
  }

  onSizesChange(event: any) {
    const selectedIds: number[] = event.value || [];
    selectedIds.forEach(id => {
      if (!this.sizeDetailsMap[id]) {
        this.sizeDetailsMap[id] = {
          quantity: 0,
          price: this.product.price || 0
        };
      }
    });
  }

  getSizeName(sizeId: number): string {
    const s = this.sizesList.find(x => x.id === sizeId);
    return s ? s.name : 'Talla';
  }

  get calculatedTotalStock(): number {
    if (!this.product.sizeIds || this.product.sizeIds.length === 0) return 0;
    return this.product.sizeIds.reduce((acc: number, id: number) => acc + (this.sizeDetailsMap[id]?.quantity || 0), 0);
  }

  openNew() {
    this.product = { isActive: true, sizeIds: [], price: 0 };
    this.sizeDetailsMap = {};
    this.filteredModels = [];
    this.selectedColorId = null;
    this.submitted = false;
    this.productDialog = true;
  }

  editProduct(row: ProductAdminItem) {
    this.loadFullProduct(row.id, (prod) => this.openEdit(prod));
  }

  private openEdit(prod: Product) {
    this.product = { ...prod };
    this.product.modelId = prod.modeloId || null;
    const nestedMoneda = (prod as Product & { tipoMoneda?: { id?: number } }).tipoMoneda;
    this.product.tipoMonedaId = prod.tipoMonedaId ?? nestedMoneda?.id ?? null;

    if (this.product.categoryId) {
      this.filterModelsByCategoryId(this.product.categoryId);
    }

    this.sizeDetailsMap = {};
    if (prod.stockDetails) {
      prod.stockDetails.forEach((d: any) => {
        this.sizeDetailsMap[d.sizeId] = {
          quantity: d.quantity,
          price: d.price || prod.price
        };
      });
      this.product.sizeIds = prod.stockDetails.map((d: any) => d.sizeId);
    }

    this.selectedColorId = this.colorsList.find(c => c.hexCode === prod.color)?.id || null;
    this.productDialog = true;
    this.cdr.detectChanges();
  }

  saveProduct() {
    this.submitted = true;
    if (!this.product.name?.trim()) return;
    if (!(Number(this.product.price) > 0)) {
      this.messageService.add({ severity: 'warn', summary: 'Precio', detail: 'El precio base debe ser mayor a 0' });
      return;
    }

    if (this.selectedColorId) {
      const color = this.colorsList.find(c => c.id === this.selectedColorId);
      this.product.color = color?.hexCode;
      this.product.colorName = color?.name;
    }

    const productToSend = {
      ...this.product,
      modeloId: this.product.modelId,
      tipoMonedaId: this.product.tipoMonedaId ?? undefined,
      stockDetails: (this.product.sizeIds || []).map((id: number) => ({
        sizeId: id,
        quantity: this.sizeDetailsMap[id].quantity,
        price: this.sizeDetailsMap[id].price
      }))
    };

    const request = this.product.id
      ? this.productService.updateProduct(this.product.id, productToSend)
      : this.productService.createProduct(productToSend);

    request.subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Éxito', detail: 'Producto Guardado' });
        this.productDialog = false;
        if (this.product.id) this.loadProducts({ first: this.first, rows: this.rows });
        else this.reloadFirst();
      },
      error: (err) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.message || 'No se pudo guardar el producto' });
      }
    });
  }

  deleteProduct(product: ProductAdminItem) {
    this.confirmationService.confirm({
      message: `¿${product.isActive ? 'Desactivar' : 'Activar'} ${product.name}?`,
      header: 'Confirmar Estado',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        const obs = product.isActive ? this.productService.deleteProduct(product.id) : this.productService.activateProduct(product.id);
        obs.subscribe(() => {
          this.messageService.add({ severity: 'success', summary: 'Éxito', detail: 'Estado actualizado' });
          this.loadProducts({ first: this.first, rows: this.rows });
        });
      }
    });
  }

  manageOffer(row: ProductAdminItem) {
    this.loadFullProduct(row.id, (product) => this.openOffer(product));
  }

  private openOffer(product: Product) {
    this.selectedProductForOffer = { ...product };
    this.offerData = {
      isOfferActive: product.isOffer || false,
      offerScope: product.offerScope || 'GLOBAL',
      globalDiscountType: product.globalDiscountType || 'S/',
      globalDiscountValue: product.globalDiscountValue || 0,
      sizeSpecificOffers: product.stockDetails ? product.stockDetails.map((d: any) => ({
        sizeId: d.sizeId,
        sizeName: d.sizeName,
        price: d.price,
        discountValue: d.discountValue || 0,
        discountType: d.discountType || 'S/'
      })) : []
    };
    this.offerDialog = true;
  }

  saveOffer() {
    if (!this.selectedProductForOffer) return;

    const productToUpdate = JSON.parse(JSON.stringify(this.selectedProductForOffer));
    const isOffer = this.offerData.isOfferActive;
    const scope = this.offerData.offerScope;

    let globalType = 'S/';
    let globalValue = 0;
    let globalPriceOffer = null;

    if (isOffer) {
      if (scope === 'GLOBAL') {
        globalType = this.offerData.globalDiscountType;
        globalValue = this.offerData.globalDiscountValue || 0;

        globalPriceOffer = globalType === '%'
          ? productToUpdate.price * (1 - globalValue / 100)
          : productToUpdate.price - globalValue;
      }
    }

    const finalPayload = {
      ...productToUpdate,
      isOffer: isOffer,
      offerScope: scope,
      globalDiscountType: globalType,
      globalDiscountValue: globalValue,
      priceOffer: globalPriceOffer,
      stockDetails: (productToUpdate.stockDetails || []).map((detail: any) => {
        const specific = this.offerData.sizeSpecificOffers.find((s: any) => s.sizeId === detail.sizeId);

        return {
          ...detail,
          discountValue: (isOffer && scope === 'BY_SIZE') ? (specific?.discountValue || 0) : 0,
          discountType: (isOffer && scope === 'BY_SIZE') ? (specific?.discountType || 'S/') : 'S/'
        };
      })
    };

    this.productService.updateProduct(finalPayload.id, finalPayload).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Éxito', detail: 'Oferta actualizada correctamente' });
        this.offerDialog = false;
        this.loadProducts({ first: this.first, rows: this.rows });
      },
      error: (err) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.message || 'No se pudo guardar la oferta' });
      }
    });
  }

  showProductDetails(row: ProductAdminItem) {
    this.loadFullProduct(row.id, (product) => {
      const modelFound = this.modelsList.find(m => m.id === (product.modeloId || product.modelId));
      this.selectedProductView = {
        ...product,
        modelNameDisplay: product.modeloName || (modelFound ? modelFound.name : 'No especificado')
      };
      this.viewProductDialog = true;
    });
  }

  showPriceDetails(row: ProductAdminItem) {
    this.loadFullProduct(row.id, (product) => {
      this.selectedProductView = product;
      this.priceDetailsDialog = true;
    });
  }

  clearFilters() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.search = '';
    this.activeFilter = null;
    this.reloadFirst();
  }

  private loadFullProduct(id: number, onReady: (product: Product) => void) {
    this.productService.getProductById(id).subscribe({
      next: (product) => {
        onReady(product);
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: err?.error?.message || 'No se pudo cargar el producto'
        });
        this.cdr.detectChanges();
      }
    });
  }

  hideDialog() {
    this.productDialog = false;
    this.submitted = false;
  }
}